import React, { useEffect, useState, useCallback } from "react";
import { BMP_MonthlyInvestment } from "../../models/bmp.model";
import { getBMPMonthlyInvestments } from "../../api/getBMPMonthlyInvestments";
import { AddMonthlyInvestmentModal } from "./modals/AddMonthlyInvestmentModal";
import { responseHero } from "../../utils/axiosInterceptor";
import { toast } from "react-toastify";
import { EmptyIcon } from "./icons/EmptyIcon";
import { PlusIcon } from "./icons/PlusIcon";
import { ArrowLeftIcon } from "./icons/ArrowLeftIcon";
import { TrashIcon } from "./icons/TrashIcon";
import Decimal from "decimal.js";

interface MonthlyInvestmentTableProps {
    onBack?: () => void;
}

export const MonthlyInvestmentTable: React.FC<MonthlyInvestmentTableProps> = ({ onBack }) => {
    // --- State ---
    const [groupedData, setGroupedData] = useState<Record<number, BMP_MonthlyInvestment[]>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [isDeletingId, setIsDeletingId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // --- Data Fetching & Grouping ---
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await getBMPMonthlyInvestments();

            // Group by monthCounter
            const groups = result.reduce((acc: Record<number, BMP_MonthlyInvestment[]>, item) => {
                const counter = item.monthCounter;
                if (!acc[counter]) acc[counter] = [];
                acc[counter].push(item);
                return acc;
            }, {});

            // Sort items within each group by date (descending)
            Object.keys(groups).forEach(key => {
                groups[Number(key)].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
            });

            setGroupedData(groups);
        } catch (error) {
            console.error("Error fetching monthly investments:", error);
            toast.error("Hiba történt az adatok letöltésekor.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- Handlers ---
    const handleAddClick = () => {
        setIsModalOpen(true);
    };

    const handleModalSubmit = async (formData: any) => {
        try {
            await responseHero.post(`${process.env.REACT_APP_API_URL}/bmp/monthlyinvestments`, { data: formData });
            toast.success(`Sikeresen rögzítetted a havi befektetést!`);
            setIsModalOpen(false); // Close modal on success
            await fetchData(); 
        } catch (error: any) {
            console.error("Error creating monthly investment:", error);
            toast.error("Sikertelen adatrögzítés!");
        }
    };

    const handleDeleteClick = async (id: number) => {
        if (!window.confirm("Biztosan törölni szeretnéd ezt a havi tételt?")) return;
        setIsDeletingId(id);
        try {
            await responseHero.delete(`${process.env.REACT_APP_API_URL}/bmp/monthlyinvestments/${id}`);
            toast.success("Sikeres törlés!");
            await fetchData();
        } catch (error) {
            toast.error("Hiba történt a törlés során.");
        } finally {
            setIsDeletingId(null);
        }
    };

    // --- Helpers ---
    
    // FIX: Standardize Decimal to Number conversion for formatting
    const toNum = (val: any) => (val ? new Decimal(val).toNumber() : 0);

    const formatMoney = (amount: any) => {
        return new Intl.NumberFormat("hu-HU", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(toNum(amount));
    };

    const formatDate = (date: string | Date) => {
        return new Date(date).toLocaleDateString("hu-HU", {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Logic for Footer Totaling
    const calculateMonthTotal = (items: BMP_MonthlyInvestment[]) => {
        return items.reduce((sum, i) => {
            const val = i.investedUsd ? new Decimal(i.investedUsd) : new Decimal(0);
            return sum.plus(val);
        }, new Decimal(0)).toNumber();
    };

    const sortedMonthKeys = Object.keys(groupedData)
        .map(Number)
        .sort((a, b) => b - a);

    return (
        <div className="min-h-screen bg-back p-6 sm:p-10 font-sans text-dark-blue">
            
            {/* Header Section */}
            <div className="max-w-6xl mx-auto mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <button 
                        onClick={onBack} 
                        className="flex items-center gap-2 text-sm font-bold text-brown hover:text-dark-brown transition-colors mb-2 uppercase tracking-wide"
                    >
                        <ArrowLeftIcon />
                        Vissza
                    </button>
                    <h1 className="text-3xl font-extrabold text-dark-blue tracking-tight">Havi Befizetések</h1>
                    <p className="text-light-blue mt-1">Havi bontásban rögzített befektetési adatok.</p>
                </div>

                <button
                    onClick={handleAddClick}
                    className="group flex items-center justify-center gap-2 bg-brown text-white px-5 py-2.5 rounded-lg shadow-md hover:bg-dark-brown hover:shadow-lg transition-all active:scale-95"
                >
                    <PlusIcon />
                    <span className="font-semibold">Új havi rögzítés</span>
                </button>
            </div>

            <div className="max-w-6xl mx-auto flex flex-col gap-10">
                {isLoading && sortedMonthKeys.length === 0 ? (
                    <div className="flex justify-center p-20">
                        <div className="animate-spin h-10 w-10 border-4 border-brown border-t-transparent rounded-full" />
                    </div>
                ) : sortedMonthKeys.length === 0 ? (
                    <div className="bg-white rounded-2xl p-16 text-center shadow-sm border border-cream">
                        <EmptyIcon />
                        <h3 className="text-lg font-semibold mt-4 text-dark-blue">Nincsenek adatok.</h3>
                        <p className="text-light-blue">Kattints az "Új havi rögzítés" gombra az induláshoz.</p>
                    </div>
                ) : (
                    sortedMonthKeys.map((month) => (
                        <div key={month} className="space-y-4">
                            <div className="flex items-center gap-4">
                                <span className="bg-brown text-white px-4 py-1 rounded-full font-bold text-sm shadow-sm">
                                    {month}. Hónap
                                </span>
                                <div className="h-px bg-cream flex-grow" />
                            </div>

                            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-cream">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-cream/50 text-dark-brown">
                                            <th className="p-4 font-bold text-xs uppercase tracking-wider">Ügyfél</th>
                                            <th className="p-4 font-bold text-xs uppercase tracking-wider">Dátum</th>
                                            <th className="p-4 font-bold text-xs uppercase tracking-wider text-right">Befektetett összeg</th>
                                            <th className="p-4 font-bold text-xs uppercase tracking-wider text-right w-24">Művelet</th>
                                        </tr>
                                    </thead>
                                    
                                    <tbody className="divide-y divide-ultra-light">
                                    {groupedData[month].map((item) => (
                                        <tr 
                                            key={item.id} 
                                            className={`transition-colors group ${
                                                item.investedFlag 
                                                    ? "bg-green-50/50 hover:bg-green-100/50" 
                                                    : "hover:bg-ultra-light/30"
                                            }`}
                                        >
                                            <td className="p-4 font-medium text-dark-blue">
                                                <div className="flex flex-col">
                                                    <span>{item.user?.name || 'Ismeretlen'}</span>
                                                    {item.investedFlag && (
                                                        <span className="text-[10px] uppercase font-bold text-green-600 tracking-tight">
                                                            Befektetve ✓
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4 text-light-blue">{formatDate(item.date)}</td>
                                            <td className={`p-4 text-right font-bold font-mono ${
                                                item.investedFlag ? "text-green-700" : "text-dark-blue"
                                            }`}>
                                                {formatMoney(item.investedUsd)}
                                            </td>
                                            <td className="p-4 text-right">
                                                <button 
                                                    onClick={() => handleDeleteClick(item.id)}
                                                    disabled={isDeletingId === item.id}
                                                    className="text-error hover:bg-error/10 p-2 rounded-lg transition-colors inline-flex items-center justify-center"
                                                    title="Törlés"
                                                >
                                                    {isDeletingId === item.id ? (
                                                        <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                                                    ) : (
                                                        <TrashIcon />
                                                    )}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>

                                    <tfoot>
                                        <tr className="bg-ultra-light/20 font-bold border-t border-cream">
                                            <td colSpan={2} className="p-4 text-xs uppercase tracking-wider text-light-blue">Havi összesen:</td>
                                            <td className="p-4 text-right text-brown text-lg font-mono">
                                                {formatMoney(calculateMonthTotal(groupedData[month]))}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <AddMonthlyInvestmentModal
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSubmit={handleModalSubmit} 
            />
        </div>
    );
};